import {} from "@/api/schema";
import { bytesToString } from "@/utils/utils";
import {} from "./CleanupOperation";
/**
 * Provides additional information about the result of the cleaning operation.
 */
export class CleanupResult {
    /**
     * Creates a new CleanupResult.
     * @param data The server response data for a cleanup operation.
     * @param items The list of requested items to be cleaned.
     * @param errorMessage A possible error message if the request completely failed.
     */
    constructor(data, items, errorMessage) {
        Object.defineProperty(this, "_data", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_item_id_map", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_errorMessage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this._data = data !== null && data !== void 0 ? data : { total_item_count: 0, success_item_count: 0, total_free_bytes: 0, errors: [] };
        items = items !== null && items !== void 0 ? items : [];
        this._item_id_map = new Map();
        items.forEach((item) => {
            this._item_id_map.set(item.id, item);
        });
        this._errorMessage = errorMessage;
    }
    /**
     * The total number of items requested to be cleaned.
     */
    get totalItemCount() {
        return this._data.total_item_count;
    }
    /**
     * The total amount of space in bytes recovered after trying to cleanup all the requested items.
     */
    get totalFreeBytes() {
        return this._data.total_free_bytes;
    }
    /**
     * List of error messages associated with a particular item ID.
     * When an item cannot be cleaned it will be registered in this list.
     */
    get errors() {
        return this._data.errors.map((e) => {
            var _a, _b;
            return { name: (_b = (_a = this._item_id_map.get(e.item_id)) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : "Unknown", reason: e.error };
        });
    }
    /**
     * A general error message, usually indicating that the whole cleanup operation failed with
     * no partial success.
     */
    get errorMessage() {
        return this._errorMessage;
    }
    /**
     * Whether the cleanup operation could not success for every requested item.
     * It doesn't mean the operation completely failed, some items could have been successfully cleaned.
     */
    get hasSomeErrors() {
        return this._data.errors.length > 0;
    }
    /**
     * Whether the cleanup operation completely failed.
     * This means not even partial cleaning was made.
     */
    get hasFailed() {
        return Boolean(this._errorMessage);
    }
    /**
     * Whether the cleanup operation was executed without errors.
     */
    get success() {
        return !this.hasSomeErrors && !this.hasFailed;
    }
    /**
     * The number of items successfully cleaned.
     */
    get totalCleaned() {
        return this._data.success_item_count;
    }
    /**
     * Whether the cleanup operation managed to free some items but not all of them.
     */
    get isPartialSuccess() {
        return this.hasSomeErrors && this._data.success_item_count > 0;
    }
    /**
     * Whether the cleanup operation managed to free some space or remove some items.
     * It can happen that only "copy" items were removed effectively recovering 0 bytes.
     */
    get hasUpdatedResults() {
        return this.totalFreeBytes > 0 || this.totalCleaned > 0;
    }
    /**
     * The total amount of disk space freed by the cleanup operation.
     */
    get niceTotalFreeBytes() {
        return bytesToString(this._data.total_free_bytes, true, undefined);
    }
}
